import routeReconstructionEndpoints from './endpoints/routeReconstruction.endpoints';

/**
 * Get reconstructed route history for a person.
 * @param {number} personId - The person/alien identifier.
 * @param {number} [hours=24] - Hours of history to reconstruct.
 * @param {boolean} [simplify=true] - Whether to simplify the route.
 * @returns {Promise<Object|null>} Route data with points and stops.
 */
export async function getRouteHistory(personId, hours = 24, simplify = true) {
  try {
    const response = await routeReconstructionEndpoints.getHistory({ params: { person_id: personId, hours, simplify } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[getRouteHistory] Error:', error);
    return null;
  }
}

/**
 * Format route time for display.
 * @param {string} isoString - ISO timestamp string.
 * @returns {string} Formatted time string.
 */
export function formatRouteTime(isoString) {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format route date for display.
 * @param {string} isoString - ISO timestamp string.
 * @returns {string} Formatted date string.
 */
export function formatRouteDate(isoString) {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format route datetime for display.
 * @param {string} isoString - ISO timestamp string.
 * @returns {string} Formatted datetime string.
 */
export function formatRouteDateTime(isoString) {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get heading arrow for direction.
 * @param {number} headingDegrees - Heading in degrees (0-360).
 * @returns {Object} Arrow character and cardinal direction.
 */
export function getHeadingDisplay(headingDegrees) {
  if (headingDegrees === null || headingDegrees === undefined) {
    return { arrow: '', direction: 'N/A' };
  }

  const directions = [
    { min: 337.5, max: 360, arrow: '\u2191', direction: 'N' },
    { min: 0, max: 22.5, arrow: '\u2191', direction: 'N' },
    { min: 22.5, max: 67.5, arrow: '\u2197', direction: 'NE' },
    { min: 67.5, max: 112.5, arrow: '\u2192', direction: 'E' },
    { min: 112.5, max: 157.5, arrow: '\u2198', direction: 'SE' },
    { min: 157.5, max: 202.5, arrow: '\u2193', direction: 'S' },
    { min: 202.5, max: 247.5, arrow: '\u2199', direction: 'SW' },
    { min: 247.5, max: 292.5, arrow: '\u2190', direction: 'W' },
    { min: 292.5, max: 337.5, arrow: '\u2196', direction: 'NW' },
  ];

  for (const dir of directions) {
    if (headingDegrees >= dir.min && headingDegrees < dir.max) {
      return { arrow: dir.arrow, direction: dir.direction };
    }
  }

  return { arrow: '\u2191', direction: 'N' };
}

/**
 * Get stop icon based on duration.
 * @param {number} durationMins - Stop duration in minutes.
 * @returns {Object} Icon and color configuration.
 */
export function getStopConfig(durationMins) {
  if (durationMins < 15) {
    return { icon: '\u23F8', color: '#6B7280', label: 'Brief Stop' };
  } else if (durationMins < 60) {
    return { icon: '\uD83D\uDCCD', color: '#3B82F6', label: 'Short Stop' };
  } else if (durationMins < 240) {
    return { icon: '\uD83C\uDFE0', color: '#22C55E', label: 'Medium Stop' };
  } else {
    return { icon: '\uD83C\uDFE8', color: '#8B5CF6', label: 'Extended Stop' };
  }
}

/**
 * Get speed color for route segment.
 * @param {number} speedMph - Speed in mph.
 * @returns {string} Color hex code.
 */
export function getRouteSegmentColor(speedMph) {
  if (!speedMph || speedMph < 5) return '#6B7280';   // Gray - stationary
  if (speedMph < 25) return '#22C55E';                // Green - slow
  if (speedMph < 45) return '#3B82F6';                // Blue - moderate
  if (speedMph < 65) return '#F59E0B';                // Amber - fast
  if (speedMph < 85) return '#F97316';                // Orange - very fast
  return '#EF4444';                                    // Red - speeding
}

/**
 * Interpolate position for playback.
 * @param {Array} points - Route points array.
 * @param {number} progress - Progress from 0 to 1.
 * @returns {Object} Interpolated position with lat, lng, heading.
 */
export function interpolatePosition(points, progress) {
  if (!points || points.length === 0) {
    return { latitude: 0, longitude: 0, heading: 0 };
  }

  if (points.length === 1 || progress <= 0) {
    return {
      latitude: points[0].latitude,
      longitude: points[0].longitude,
      heading: points[0].heading_degrees || 0,
    };
  }

  if (progress >= 1) {
    const last = points[points.length - 1];
    return {
      latitude: last.latitude,
      longitude: last.longitude,
      heading: last.heading_degrees || 0,
    };
  }

  // Find the segment for the current progress
  const totalIndex = progress * (points.length - 1);
  const segmentIndex = Math.floor(totalIndex);
  const segmentProgress = totalIndex - segmentIndex;

  const startPoint = points[segmentIndex];
  const endPoint = points[Math.min(segmentIndex + 1, points.length - 1)];

  // Linear interpolation
  return {
    latitude: startPoint.latitude + (endPoint.latitude - startPoint.latitude) * segmentProgress,
    longitude: startPoint.longitude + (endPoint.longitude - startPoint.longitude) * segmentProgress,
    heading: endPoint.heading_degrees || startPoint.heading_degrees || 0,
  };
}

/**
 * Calculate route bounds for map fitting.
 * @param {Array} points - Route points array.
 * @returns {Array} Leaflet bounds [[south, west], [north, east]].
 */
export function calculateRouteBounds(points) {
  if (!points || points.length === 0) {
    return [[29.7, -95.4], [29.8, -95.3]]; // Default Houston area
  }

  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;

  for (const point of points) {
    if (point.latitude < minLat) minLat = point.latitude;
    if (point.latitude > maxLat) maxLat = point.latitude;
    if (point.longitude < minLng) minLng = point.longitude;
    if (point.longitude > maxLng) maxLng = point.longitude;
  }

  // Add some padding
  const latPadding = (maxLat - minLat) * 0.1 || 0.01;
  const lngPadding = (maxLng - minLng) * 0.1 || 0.01;

  return [
    [minLat - latPadding, minLng - lngPadding],
    [maxLat + latPadding, maxLng + lngPadding],
  ];
}
